import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from '@studio-freight/lenis';
import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingCommunication from "./components/FloatingCommunication";
import ScrollToTop from "./components/ScrollToTop";
import LoadingScreen from "./components/LoadingScreen";
import AIChatbot from "./components/AIChatbot";
import Home from "./pages/Home";
import ProductDetail from "./pages/ProductDetail";
import Products from "./pages/Products";
import Applications from "./pages/Applications";
import About from "./pages/About";
import Dealer from "./pages/Dealer";
import Contact from "./pages/Contact";
import DownloadBrochure from "./pages/DownloadBrochure";
import PURTechnology from "./pages/PURTechnology";
import CarpenterChecklist from "./pages/CarpenterChecklist";
import WhyFactoryLaminated from "./pages/WhyFactoryLaminated";
import CustomerStories from "./pages/CustomerStories";
import ApplicationBenefits from "./pages/ApplicationBenefits";
import AIVisualizer from "./pages/AIVisualizer";
import AIDoorDesigner from "./pages/AIDoorDesigner";
import AIProductRecommender from "./pages/AIProductRecommender";
import DesignGallery from "./pages/DesignGallery";
import MaterialLanding from "./pages/MaterialLanding";
import NailFreeAdvantage from "./pages/NailFreeAdvantage";
import MatchMyDoors from "./pages/MatchMyDoors";
import KnowledgePage from "./pages/KnowledgePage";
import ComparisonPage from "./pages/ComparisonPage";
import CityPage from "./pages/CityPage";
import { BlogIndex, BlogPost } from "./pages/Blog";
import FounderPage from "./pages/FounderPage";
import CrilamFacts from "./pages/CrilamFacts";
import AskCrilam from "./pages/AskCrilam";
import NotFound from "./pages/NotFound";
import { KNOWLEDGE_ARTICLES, COMPARISON_PAGES, UP_CITIES, BLOG_ARTICLES } from "./lib/seoContent";
import { Toaster } from "sonner";

// Layout wrapper that conditionally shows header/footer
const Layout = ({ children }) => {
  const location = useLocation();
  const staticPaths = [
    '/',
    '/products',
    '/applications',
    '/about',
    '/dealer',
    '/contact',
    '/download-brochure',
    '/pur-technology',
    '/ask-your-carpenter',
    '/why-factory-laminated',
    '/customer-stories',
    '/application-benefits',
    '/ai-visualizer',
    '/door-designer',
    '/product-recommender',
    '/design-gallery',
    '/mica-cnc-doors',
    '/veneer-nail-free-doors',
    '/veneer-cnc-doors',
    '/pu-paint-doors',
    '/acrylic-laminate-doors',
    '/nail-free-advantage',
    '/match-my-doors',
    '/blog',
    '/founder',
    '/crilam-facts',
    '/ask-crilam',
  ];
  // GEO-generated paths (flat URLs matching sitemap.xml)
  const knowledgePaths = Object.keys(KNOWLEDGE_ARTICLES).map((s) => `/${s}`);
  const comparisonPaths = Object.keys(COMPARISON_PAGES).map((s) => `/${s}`);
  const cityPaths = UP_CITIES.map((c) => `/acrylic-boards-${c.slug}`);
  const blogPaths = BLOG_ARTICLES.map((b) => `/blog/${b.slug}`);

  const validPaths = [...staticPaths, ...knowledgePaths, ...comparisonPaths, ...cityPaths, ...blogPaths];

  const is404 = !validPaths.some(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  if (is404) {
    return children;
  }

  return (
    <>
      <Header />
      {children}
      <Footer />
    </>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  // Initialize Lenis smooth scrolling
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Make lenis available globally for other components
    window.lenis = lenis;

    return () => {
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  // Loading screen timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 second loading screen

    return () => clearTimeout(timer);
  }, []);

  // Show loading screen on initial load
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/dealer-distributor" element={<Navigate to="/dealer" replace />} />
            <Route path="/applications" element={<Applications />} />
            <Route path="/about" element={<About />} />
            <Route path="/dealer" element={<Dealer />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/download-brochure" element={<DownloadBrochure />} />
            <Route path="/pur-technology" element={<PURTechnology />} />
            <Route path="/ask-your-carpenter" element={<CarpenterChecklist />} />
            <Route path="/why-factory-laminated" element={<WhyFactoryLaminated />} />
            <Route path="/customer-stories" element={<CustomerStories />} />
            <Route path="/application-benefits" element={<ApplicationBenefits />} />
            <Route path="/ai-visualizer" element={<AIVisualizer />} />
            <Route path="/door-designer" element={<AIDoorDesigner />} />
            <Route path="/product-recommender" element={<AIProductRecommender />} />
            <Route path="/design-gallery" element={<DesignGallery />} />
            <Route path="/acrylic-laminate-doors" element={<MaterialLanding materialId="acrylic-laminate" />} />
            <Route path="/mica-cnc-doors" element={<MaterialLanding materialId="mica" cncFocus={true} />} />
            <Route path="/veneer-nail-free-doors" element={<MaterialLanding materialId="veneer" />} />
            <Route path="/veneer-cnc-doors" element={<MaterialLanding materialId="veneer" cncFocus={true} />} />
            <Route path="/pu-paint-doors" element={<MaterialLanding materialId="pu-paint" />} />
            <Route path="/nail-free-advantage" element={<NailFreeAdvantage />} />
            <Route path="/match-my-doors" element={<MatchMyDoors />} />

            {/* GEO — Knowledge / Q&A articles (flat URLs matching sitemap.xml) */}
            {Object.keys(KNOWLEDGE_ARTICLES).map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<KnowledgePage slug={slug} />} />
            ))}

            {/* GEO — Comparison studies */}
            {Object.keys(COMPARISON_PAGES).map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<ComparisonPage slug={slug} />} />
            ))}

            {/* GEO — UP City landing pages */}
            {UP_CITIES.map((c) => (
              <Route key={c.slug} path={`/acrylic-boards-${c.slug}`} element={<CityPage citySlug={c.slug} />} />
            ))}

            {/* GEO — Blog / Journal */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* GEO — E-E-A-T + Authority pages */}
            <Route path="/founder" element={<FounderPage />} />
            <Route path="/crilam-facts" element={<CrilamFacts />} />
            <Route path="/ask-crilam" element={<AskCrilam />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
        <FloatingCommunication />
        <AIChatbot />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#0A0D0C',
              color: '#F4F5F4',
              border: '1px solid rgba(255,255,255,0.1)',
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
